// Hospital-monitor-style panel: 3 waveform lanes (ECG II, capnography, pleth) + numeric vitals strip.
// Pure presentational component — state passed in from the room client.

import WaveformLane from './WaveformLane';
import type { Rhythm } from '../../lib/waveforms';

export interface MonitorState {
  rhythm: Rhythm;
  hr: number;
  sbp: number;
  dbp: number;
  spo2: number;
  rr: number;
  etco2_mmhg: number;
  temp_c: number;
  sim_t_ms: number;
  cpr_active: boolean;
  cpr_quality_pct: number;
  cpr_rate_cpm: number;
  intubated: boolean;
  shock_at_ms?: number;
}

const RHYTHM_LABEL: Record<Rhythm, string> = {
  sinus: 'Sinus', sinus_tach: 'ST', brady: 'Brady',
  af: 'AF', aflutter: 'AFl', svt: 'SVT',
  vt_pulse: 'VT', vt_pulseless: 'pVT', vf: 'VF', torsades: 'TdP',
  asystole: 'Asys', p_wave_asystole: 'Asys (P+)',
  pea_narrow: 'PEA', pea_broad: 'PEA',
  av1: '1° AVB', chb: 'CHB',
  idioventricular: 'IVR', agonal: 'Agonal',
};

function isAlarm(rh: Rhythm): boolean {
  return ['vf', 'vt_pulseless', 'asystole', 'p_wave_asystole', 'pea_narrow', 'pea_broad', 'torsades', 'agonal'].includes(rh);
}

export default function MonitorPanel({ state }: { state: MonitorState }) {
  const vitals = {
    rhythm: state.rhythm,
    hr: state.hr,
    spo2: state.spo2,
    etco2_mmhg: state.etco2_mmhg,
    rr: state.rr,
  };

  return (
    <div className="panel p-3 select-none">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-mono-white/60 text-xs uppercase tracking-widest">Monitor</h3>
        {isAlarm(state.rhythm) && (
          <span className="alarm-banner bg-alarm-red/20 text-alarm-red ring-1 ring-alarm-red/50">
            ALARM · {RHYTHM_LABEL[state.rhythm]}
          </span>
        )}
      </div>

      <Lane title="ECG II" color="mono-green" right={`HR ${state.hr > 0 ? state.hr : (state.cpr_active && state.cpr_rate_cpm >= 20 ? `${state.cpr_rate_cpm}*` : '--')}`}>
        <WaveformLane lane="ecg" vitals={vitals} sim_t_ms={state.sim_t_ms} cpr_active={state.cpr_active} cpr_quality_pct={state.cpr_quality_pct} cpr_rate_cpm={state.cpr_rate_cpm} intubated={state.intubated} shock_at_ms={state.shock_at_ms} />
      </Lane>

      <Lane title="EtCO₂" color="mono-cyan" right={`${state.etco2_mmhg.toFixed(0)} mmHg`}>
        <WaveformLane lane="capno" vitals={vitals} sim_t_ms={state.sim_t_ms} cpr_active={state.cpr_active} cpr_quality_pct={state.cpr_quality_pct} cpr_rate_cpm={state.cpr_rate_cpm} intubated={state.intubated} />
      </Lane>

      <Lane title="SpO₂ pleth" color="mono-yellow" right={`${state.spo2 || '--'}%`}>
        <WaveformLane lane="pleth" vitals={vitals} sim_t_ms={state.sim_t_ms} cpr_active={state.cpr_active} cpr_quality_pct={state.cpr_quality_pct} cpr_rate_cpm={state.cpr_rate_cpm} intubated={state.intubated} />
      </Lane>

      <div className="grid grid-cols-4 gap-3 mt-3">
        <Vital
          label="HR"
          value={state.hr > 0 ? state.hr : (state.cpr_active && state.cpr_rate_cpm >= 20 ? state.cpr_rate_cpm : '--')}
          unit={state.hr > 0 ? 'bpm' : (state.cpr_active && state.cpr_rate_cpm >= 20 ? 'cpm·CPR' : 'bpm')}
          color={state.hr === 0 && state.cpr_active && (state.cpr_rate_cpm < 100 || state.cpr_rate_cpm > 120) ? 'alarm-amber' : 'mono-green'}
        />
        <Vital label="BP" value={state.sbp ? `${state.sbp}/${state.dbp}` : '--/--'} unit="mmHg" color="mono-white" />
        <Vital label="SpO₂" value={state.spo2 || '--'} unit="%" color="mono-yellow" />
        <Vital label="RR" value={state.rr || '--'} unit="/min" color="mono-white" />
        <Vital label="EtCO₂" value={state.etco2_mmhg.toFixed(0)} unit="mmHg" color="mono-cyan" />
        <Vital label="Temp" value={state.temp_c.toFixed(1)} unit="°C" color="mono-white" />
        <Vital label="Rhythm" value={RHYTHM_LABEL[state.rhythm]} unit="" color={isAlarm(state.rhythm) ? 'alarm-red' : 'mono-green'} />
        <Vital label="T+" value={`${(state.sim_t_ms / 1000).toFixed(0)}s`} unit="" color="mono-white" />
      </div>
      {state.cpr_active && state.hr === 0 && (
        <p className="text-[10px] text-mono-white/40 mt-2 italic">
          * HR shown is your live compression rate — no intrinsic rhythm in arrest. Target 100–120 cpm. BP, SpO₂ and pleth are CPR-derived.
        </p>
      )}
    </div>
  );
}

function Lane({ title, color, right, children }: { title: string; color: string; right: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-mono-white/60 mb-1 font-mono">
        <span className={`text-${color}`}>{title}</span>
        <span className={`text-${color} font-semibold`}>{right}</span>
      </div>
      {children}
    </div>
  );
}

function Vital({ label, value, unit, color }: { label: string; value: any; unit: string; color: string }) {
  return (
    <div>
      <div className="text-mono-white/40 text-[10px] uppercase tracking-widest">{label}</div>
      <div className={`vital-num text-${color}`}>{value}<span className="text-xs ml-1 text-mono-white/40">{unit}</span></div>
    </div>
  );
}
